import { Link } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowRight, CircleAlert, CircleCheck, RotateCcw } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TARGET_SHARE = 0.28 // healthy ticket share of total revenue
const CAPS = [1, 2, 4, 6, 30] // max sponsors per tier: Presenting, Gold, Silver, Bronze, Friend

type ScaleKey = 'small' | 'mid' | 'large' | 'premium'

type Tier = { name: string; amount: number; seats: number }

type ScaleConfig = {
  label: string
  ceiling: number
  floorPrice: number
  tiers: Tier[]
  shape: number[]
}

const SHAPE = [1, 2, 3, 4, 6]

const SCALES: Record<ScaleKey, ScaleConfig> = {
  small: {
    label: 'small community benefit',
    ceiling: 300,
    floorPrice: 40,
    tiers: [
      { name: 'Presenting', amount: 10000, seats: 10 },
      { name: 'Gold', amount: 5000, seats: 8 },
      { name: 'Silver', amount: 2500, seats: 4 },
      { name: 'Bronze', amount: 1000, seats: 2 },
      { name: 'Friend', amount: 500, seats: 2 },
    ],
    shape: SHAPE,
  },
  mid: {
    label: 'mid-size gala',
    ceiling: 600,
    floorPrice: 75,
    tiers: [
      { name: 'Presenting', amount: 25000, seats: 16 },
      { name: 'Gold', amount: 10000, seats: 10 },
      { name: 'Silver', amount: 5000, seats: 8 },
      { name: 'Bronze', amount: 2500, seats: 4 },
      { name: 'Friend', amount: 1000, seats: 2 },
    ],
    shape: SHAPE,
  },
  large: {
    label: 'large gala',
    ceiling: 1000,
    floorPrice: 100,
    tiers: [
      { name: 'Presenting', amount: 50000, seats: 20 },
      { name: 'Gold', amount: 25000, seats: 10 },
      { name: 'Silver', amount: 10000, seats: 8 },
      { name: 'Bronze', amount: 5000, seats: 4 },
      { name: 'Friend', amount: 2500, seats: 2 },
    ],
    shape: SHAPE,
  },
  premium: {
    label: 'premium / marquee event',
    ceiling: 1500,
    floorPrice: 150,
    tiers: [
      { name: 'Presenting', amount: 100000, seats: 20 },
      { name: 'Gold', amount: 50000, seats: 10 },
      { name: 'Silver', amount: 25000, seats: 8 },
      { name: 'Bronze', amount: 10000, seats: 4 },
      { name: 'Friend', amount: 5000, seats: 2 },
    ],
    shape: SHAPE,
  },
}

const SCALE_ORDER: { key: ScaleKey; label: string; blurb: string }[] = [
  { key: 'small', label: 'Small', blurb: 'Community benefit' },
  { key: 'mid', label: 'Mid-size', blurb: 'Mid-size gala' },
  { key: 'large', label: 'Large', blurb: 'Large gala' },
  { key: 'premium', label: 'Premium', blurb: 'Marquee event' },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const money = (n: number) => currency.format(Number.isFinite(n) ? n : 0)

const num = (v: string | number): number => {
  const n = typeof v === 'number' ? v : parseFloat(v)
  return Number.isFinite(n) && n > 0 ? n : 0
}

const toInt = (v: string): number => {
  const n = parseInt(v, 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

const round5 = (n: number) => Math.max(0, Math.round(n / 5) * 5)
const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), hi)

type LadderRow = { name: string; amount: number; seats: number; count: number }

type Scalars = {
  goal: string
  cost: string
  indiv: string
  ads: string
  auction: string
  donations: string
  fee: string
}

const DEFAULT_SCALARS: Scalars = {
  goal: '60000',
  cost: '35700',
  indiv: '168',
  ads: '4000',
  auction: '8000',
  donations: '3000',
  fee: '3',
}

const keepFrom = (fee: string) => 1 - Math.min(num(fee), 20) / 100
const otherNetFrom = (s: Scalars) => num(s.ads) + num(s.auction) + num(s.donations)

const priceForScale = (
  cfg: ScaleConfig,
  costN: number,
  goalN: number,
  indivN: number,
  keep: number,
): number => {
  if (indivN <= 0) return 0
  const raw = (TARGET_SHARE * (costN + goalN)) / indivN / (keep > 0 ? keep : 1)
  return Math.max(0, clamp(round5(raw), cfg.floorPrice, cfg.ceiling))
}

// Ladder auto-sizing: (re)fill each row's count so the ladder closes the gap,
// respecting CAPS, then greedily top up where rounding on big tiers undershoots.
const suggestCounts = (rows: LadderRow[], cfg: ScaleConfig, s: Scalars): number[] => {
  const costN = num(s.cost)
  const goalN = num(s.goal)
  const indivN = num(s.indiv)
  const keep = keepFrom(s.fee)
  const otherNet = otherNetFrom(s)

  const price = priceForScale(cfg, costN, goalN, indivN, keep)
  const ticketNet = price * indivN * keep
  const gapS = Math.max(0, costN + goalN - ticketNet - otherNet)

  const amounts = rows.map((r) => r.amount)
  const shape = cfg.shape
  const caps = rows.map((_, i) => CAPS[i] ?? 30)

  const unit = amounts.reduce((sum, a, i) => sum + a * shape[i], 0)
  const mult = unit > 0 ? gapS / unit : 0
  const counts = amounts.map((_, i) => Math.min(caps[i], Math.max(0, Math.round(shape[i] * mult))))
  let total = amounts.reduce((sum, a, i) => sum + a * counts[i], 0)

  let guard = 0
  while (total < gapS - 1 && guard < 2000) {
    const remaining = gapS - total
    let pick = -1
    for (let i = 0; i < amounts.length; i++) {
      if (amounts[i] > 0 && counts[i] < caps[i] && amounts[i] <= remaining) {
        pick = i
        break
      }
    }
    if (pick === -1) {
      // nothing fits cleanly — bump the smallest-amount tier still under its cap
      let sm = -1
      for (let i = 0; i < amounts.length; i++) {
        if (amounts[i] > 0 && counts[i] < caps[i] && (sm === -1 || amounts[i] < amounts[sm])) {
          sm = i
        }
      }
      if (sm === -1) break // every tier capped — ladder can't reach goal at this scale
      counts[sm]++
      total += amounts[sm]
      break
    }
    counts[pick]++
    total += amounts[pick]
    guard++
  }

  return counts
}

const buildLadder = (cfg: ScaleConfig, s: Scalars): LadderRow[] => {
  const base: LadderRow[] = cfg.tiers.map((t) => ({ ...t, count: 0 }))
  const counts = suggestCounts(base, cfg, s)
  return base.map((r, i) => ({ ...r, count: counts[i] }))
}

// ---------------------------------------------------------------------------
// Segment palette (no brand token exists for these — use the spec hex values)
// ---------------------------------------------------------------------------

const MIX_COLORS = {
  tickets: '#f3722c', // orange
  sponsorships: '#dd5500', // orange-deep
  ads: '#f8961e', // amber
  auction: '#f9c74f', // gold
  donations: '#7c6e67', // taupe
}

const PANEL = 'rounded-2xl border border-black/10 bg-card p-5 shadow-xs'
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function GalaTicketPricingPlanner() {
  const [scale, setScale] = useState<ScaleKey>('large')
  const [scalars, setScalars] = useState<Scalars>(DEFAULT_SCALARS)
  const [ladder, setLadder] = useState<LadderRow[]>(() => buildLadder(SCALES.large, DEFAULT_SCALARS))
  const [userEditedCounts, setUserEditedCounts] = useState(false)

  const cfg = SCALES[scale]

  // --- handlers ---
  const changeScale = (key: ScaleKey) => {
    setScale(key)
    setUserEditedCounts(false)
    setLadder(buildLadder(SCALES[key], scalars))
  }

  const setScalar = (key: keyof Scalars, value: string) => {
    const next = { ...scalars, [key]: value }
    setScalars(next)
    if (!userEditedCounts) {
      setLadder((rows) => {
        const counts = suggestCounts(rows, cfg, next)
        return rows.map((r, i) => ({ ...r, count: counts[i] }))
      })
    }
  }

  const editCount = (i: number, value: string) => {
    setUserEditedCounts(true)
    setLadder((rows) => rows.map((r, idx) => (idx === i ? { ...r, count: toInt(value) } : r)))
  }

  const editRowField = (i: number, key: 'name' | 'amount' | 'seats', value: string) => {
    setLadder((rows) => {
      const updated = rows.map((r, idx) =>
        idx === i ? { ...r, [key]: key === 'name' ? value : num(value) } : r,
      )
      if (!userEditedCounts) {
        const counts = suggestCounts(updated, cfg, scalars)
        return updated.map((r, idx) => ({ ...r, count: counts[idx] }))
      }
      return updated
    })
  }

  const resetCounts = () => {
    setUserEditedCounts(false)
    setLadder((rows) => {
      const counts = suggestCounts(rows, cfg, scalars)
      return rows.map((r, i) => ({ ...r, count: counts[i] }))
    })
  }

  // --- derived values (computed every render) ---
  const goalN = num(scalars.goal)
  const costN = num(scalars.cost)
  const indivN = num(scalars.indiv)
  const adsN = num(scalars.ads)
  const auctionN = num(scalars.auction)
  const donationsN = num(scalars.donations)
  const keep = 1 - Math.min(num(scalars.fee), 20) / 100
  const otherNet = adsN + auctionN + donationsN

  const spons = ladder.reduce((sum, r) => sum + r.amount * r.count, 0)
  const sponSeats = ladder.reduce((sum, r) => sum + r.seats * r.count, 0)
  const ladderFloor = ladder.reduce(
    (min, r) => (r.amount > 0 && r.seats > 0 ? Math.min(min, r.amount / r.seats) : min),
    Infinity,
  )
  const totalAtt = indivN + sponSeats
  const perGuest = totalAtt > 0 ? costN / totalAtt : 0

  const price = priceForScale(cfg, costN, goalN, indivN, keep)
  const low = clamp(round5(price * 0.8), cfg.floorPrice, price)
  const high = clamp(round5(Math.min(price * 1.2, cfg.ceiling)), price, cfg.ceiling)

  const ticketNet = price * indivN * keep
  const net = ticketNet + spons + otherNet - costN
  const gap = goalN - net
  const goalTolerance = Math.max(500, goalN * 0.01)
  const clearsGoal = gap <= goalTolerance
  const breakEven =
    indivN > 0 && keep > 0 ? round5(Math.max(0, costN - spons - otherNet) / indivN / keep) : 0
  const gross = price * indivN + spons + otherNet
  const margin = gross > 0 ? Math.round((net / gross) * 100) : 0
  const ticketGross = price * indivN

  // --- copy strings ---
  const goalCheck = (() => {
    if (clearsGoal) {
      return { tone: 'ok' as const, text: `This plan clears your ${money(goalN)} goal.` }
    }
    if (net < 0) {
      return {
        tone: 'bad' as const,
        text: 'The night loses money as planned. Trim costs or add sponsorship before locking a goal.',
      }
    }
    return {
      tone: 'warn' as const,
      text: `About ${money(gap)} short of your ${money(goalN)} goal. Close it with larger sponsorships, a bigger auction and paddle raise, more ticket sales — or step up to a larger event size.`,
    }
  })()

  const priceNote = (() => {
    let base = `≈ ${money(price)} is the balanced pick (about 28% from tickets). Price toward the low end to lean on sponsorships, the high end to ask more of guests.`
    if (price >= cfg.ceiling) {
      base = clearsGoal
        ? `≈ ${money(price)} tops out the ${cfg.label} range. The gap to your goal is closed by the sponsorship ladder below — not a higher ticket.`
        : `≈ ${money(price)} tops out the ${cfg.label} range. Close the remaining ${money(gap)} with larger sponsorships, auction, paddle raise, or donations — not a higher ticket.`
    }
    if (Number.isFinite(ladderFloor) && price > round5(ladderFloor)) {
      base += ` Note: this tops your lowest sponsor's per-seat rate (${money(ladderFloor)}) — consider nudging that tier up so sponsoring always beats a single ticket.`
    }
    return base
  })()

  const grossTotal = ticketGross + spons + adsN + auctionN + donationsN
  const ts = grossTotal > 0 ? Math.round((ticketGross / grossTotal) * 100) : 0
  const mixNote = (() => {
    if (ts > 55) {
      return `Tickets are carrying ${ts}% of the night — a lot of risk on attendance. Lean harder on sponsorships and the live program.`
    }
    if (ts <= 33) {
      return `Good balance — tickets are ${ts}% of revenue, with sponsorships and the program carrying the rest.`
    }
    return `Tickets are ${ts}% of revenue — workable, though shifting more onto sponsorships keeps you nearer the 20–30% ideal.`
  })()

  const perGuestRound = Math.round(perGuest)
  const fmvIndividual =
    indivN > 0
      ? `Don't forget fair market value. Guests deduct only the part of a ticket above the value of the meal and entertainment they receive. With an all-in cost around ${money(perGuestRound)} a guest, a ${money(price)} ticket leaves at least ${money(Math.max(0, price - perGuestRound))} deductible — likely more, since the food and entertainment are usually worth less than your full per-head cost. State the amount on receipts.`
      : null
  const fmvSponsor =
    sponSeats > 0 && perGuest > 0
      ? ` Sponsor seats reduce deductibility too: ${sponSeats} seats at that value put about ${money(sponSeats * perGuestRound)} of sponsorship dollars outside the deductible portion.`
      : null

  // --- revenue mix segments ---
  const mixSegments = [
    { label: 'Tickets', value: ticketGross, color: MIX_COLORS.tickets },
    { label: 'Sponsorships', value: spons, color: MIX_COLORS.sponsorships },
    { label: 'Program ads', value: adsN, color: MIX_COLORS.ads },
    { label: 'Auction', value: auctionN, color: MIX_COLORS.auction },
    { label: 'Donations', value: donationsN, color: MIX_COLORS.donations },
  ]

  // --- scenarios ---
  const netAt = (n: number) => price * n * keep + spons + otherNet - costN
  const scenarios = [
    { label: 'Light night', n: Math.round(indivN * 0.7) },
    { label: 'As planned', n: Math.round(indivN) },
    { label: 'Sold out', n: Math.round(indivN * 1.2) },
  ].map((s) => ({ ...s, net: netAt(s.n) }))
  const scenarioMax = Math.max(1, ...scenarios.map((s) => Math.abs(s.net)))

  const rangeLabel = low >= high ? money(price) : `${money(low)} – ${money(high)}`

  const goalTone = {
    ok: { box: 'bg-primary/10', icon: 'text-primary', Icon: CircleCheck },
    warn: { box: 'bg-secondary/20', icon: 'text-[#dd5500]', Icon: CircleAlert },
    bad: { box: 'bg-destructive/10 text-destructive', icon: 'text-destructive', Icon: CircleAlert },
  }[goalCheck.tone]

  return (
    <div className="space-y-5">
      {/* Event-scale selector */}
      <div className={PANEL}>
        <span className={EYEBROW}>What kind of event is this?</span>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {SCALE_ORDER.map((opt) => {
            const active = scale === opt.key
            return (
              <Button
                key={opt.key}
                type="button"
                variant="outline"
                onClick={() => changeScale(opt.key)}
                aria-pressed={active}
                className={cn(
                  'h-auto flex-col items-start gap-0.5 whitespace-normal px-3.5 py-2.5 text-left',
                  active
                    ? 'border-primary bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary'
                    : 'text-foreground',
                )}
              >
                <span className="text-sm font-semibold">{opt.label}</span>
                <span
                  className={cn('text-[11px] font-normal', active ? 'text-primary/80' : 'text-muted-foreground')}
                >
                  {opt.blurb}
                </span>
              </Button>
            )
          })}
        </div>
      </div>

      {/* 3-column responsive grid: Inputs | Results | Ladder */}
      <div className="grid grid-cols-1 gap-5 min-[680px]:grid-cols-2 lg:grid-cols-3">
        {/* Inputs */}
        <section className={PANEL}>
          <h2 className="text-lg font-semibold tracking-tight">Your targets</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Pick a size above, then tune the numbers to your event.
          </p>
          <div className="mt-4 space-y-4">
            <MoneyField
              id="goal"
              label="Net fundraising goal"
              value={scalars.goal}
              onChange={(v) => setScalar('goal', v)}
            />
            <MoneyField
              id="cost"
              label="Total event cost"
              value={scalars.cost}
              onChange={(v) => setScalar('cost', v)}
              hint={
                totalAtt > 0
                  ? `≈ ${money(perGuest)} per guest across ${totalAtt} seats`
                  : 'Add ticket and sponsor seats to see cost per guest'
              }
            />
            <NumberField
              id="indiv"
              label="Individual tickets sold"
              value={scalars.indiv}
              onChange={(v) => setScalar('indiv', v)}
            />

            <div className="border-t border-black/5 pt-4">
              <span className={EYEBROW}>Other revenue</span>
              <div className="mt-3 space-y-4">
                <MoneyField
                  id="ads"
                  label="Program ad sales"
                  value={scalars.ads}
                  onChange={(v) => setScalar('ads', v)}
                />
                <MoneyField
                  id="auction"
                  label="Auction & paddle raise"
                  value={scalars.auction}
                  onChange={(v) => setScalar('auction', v)}
                />
                <MoneyField
                  id="donations"
                  label="Donations & gifts"
                  value={scalars.donations}
                  onChange={(v) => setScalar('donations', v)}
                />
              </div>
            </div>

            <div className="border-t border-black/5 pt-4">
              <PercentField
                id="fee"
                label="Payment processing fee"
                value={scalars.fee}
                onChange={(v) => setScalar('fee', v)}
              />
            </div>
          </div>
        </section>

        {/* Results */}
        <section className={PANEL}>
          <span className={EYEBROW}>Suggested ticket price</span>
          <p className="mt-1 font-serif text-4xl font-semibold tracking-tight text-primary tabular-nums">
            {rangeLabel}
          </p>
          <p className="mt-2 text-sm text-foreground">{priceNote}</p>

          {/* Goal check */}
          <div className={cn('mt-5 flex gap-2.5 rounded-xl p-3 text-sm', goalTone.box)}>
            <goalTone.Icon className={cn('mt-0.5 size-4 shrink-0', goalTone.icon)} aria-hidden="true" />
            <p className="font-medium">{goalCheck.text}</p>
          </div>

          {/* Mini stats */}
          <div className="mt-5 grid grid-cols-3 gap-3 border-t border-black/5 pt-5">
            <Stat label="Break even" value={breakEven === 0 ? 'Covered' : money(breakEven)} />
            <Stat label="Seats in the room" value={totalAtt.toLocaleString('en-US')} />
            <Stat label="Net margin" value={`${margin}%`} />
          </div>

          {/* Revenue mix */}
          <div className="mt-5 border-t border-black/5 pt-5">
            <span className={EYEBROW}>Money the night comes from</span>
            <div className="mt-2.5 flex h-3 overflow-hidden rounded-full bg-muted" aria-hidden="true">
              {mixSegments.map((seg) => {
                const share = grossTotal > 0 ? seg.value / grossTotal : 0
                if (share <= 0) return null
                return (
                  <div
                    key={seg.label}
                    style={{ width: `${share * 100}%`, backgroundColor: seg.color }}
                  />
                )
              })}
            </div>
            <ul className="mt-3 space-y-1.5">
              {mixSegments.map((seg) => {
                const share = grossTotal > 0 ? seg.value / grossTotal : 0
                return (
                  <li key={seg.label} className="flex items-center gap-2 text-xs">
                    <span
                      className="size-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: seg.color }}
                      aria-hidden="true"
                    />
                    <span className="font-medium text-foreground">{seg.label}</span>
                    <span className="ml-auto tabular-nums text-muted-foreground">
                      {money(seg.value)} · {Math.round(share * 100)}%
                    </span>
                  </li>
                )
              })}
            </ul>
            <p className="mt-3 text-sm text-foreground">{mixNote}</p>
          </div>

          {/* FMV note */}
          {fmvIndividual && (
            <p className="mt-5 border-t border-black/5 pt-5 text-xs leading-relaxed text-muted-foreground">
              {fmvIndividual}
              {fmvSponsor}
            </p>
          )}
        </section>

        {/* Sponsorship ladder + scenarios */}
        <section className={cn(PANEL, 'min-[680px]:col-span-2 lg:col-span-1')}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Suggested sponsorship ladder</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">Sized for a {cfg.label}</p>
            </div>
            {userEditedCounts && (
              <Button type="button" variant="ghost" size="sm" onClick={resetCounts} className="gap-1">
                <RotateCcw className="size-3.5" aria-hidden="true" />
                Reset
              </Button>
            )}
          </div>

          <div className="mt-4">
            <div className="grid grid-cols-[1fr_5rem_3.25rem_2.75rem] items-center gap-2 border-b border-black/10 px-1 pb-2">
              <span className={EYEBROW}>Tier</span>
              <span className={cn(EYEBROW, 'text-right')}>$ each</span>
              <span className={cn(EYEBROW, 'text-right')}>Seats</span>
              <span className={cn(EYEBROW, 'text-right')}>Qty</span>
            </div>
            {ladder.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr_5rem_3.25rem_2.75rem] items-center gap-2 border-b border-black/5 py-1"
              >
                <CellInput
                  aria-label={`Tier ${i + 1} name`}
                  value={row.name}
                  onChange={(e) => editRowField(i, 'name', e.target.value)}
                />
                <CellInput
                  aria-label={`Tier ${i + 1} amount`}
                  type="number"
                  inputMode="numeric"
                  align="right"
                  value={row.amount}
                  onChange={(e) => editRowField(i, 'amount', e.target.value)}
                />
                <CellInput
                  aria-label={`Tier ${i + 1} seats`}
                  type="number"
                  inputMode="numeric"
                  align="right"
                  value={row.seats}
                  onChange={(e) => editRowField(i, 'seats', e.target.value)}
                />
                <CellInput
                  aria-label={`Tier ${i + 1} quantity`}
                  type="number"
                  inputMode="numeric"
                  align="right"
                  className="font-semibold"
                  value={row.count}
                  onChange={(e) => editCount(i, e.target.value)}
                />
              </div>
            ))}
            <div className="mt-3 flex items-center justify-between pt-1 text-sm">
              <span className="font-semibold text-primary">Sponsorships</span>
              <span className="font-serif text-base font-semibold tabular-nums">
                {money(spons)} · {sponSeats} seats
              </span>
            </div>
          </div>

          {/* Scenarios */}
          <div className="mt-5 border-t border-black/5 pt-5">
            <span className={EYEBROW}>Net proceeds if individual sales come in at…</span>
            <div className="mt-3 space-y-3">
              {scenarios.map((sc) => {
                const width = (Math.abs(sc.net) / scenarioMax) * 100
                const negative = sc.net < 0
                return (
                  <div key={sc.label}>
                    <div className="flex items-baseline justify-between text-xs">
                      <span className="font-medium text-foreground">
                        {sc.label}{' '}
                        <span className="text-muted-foreground">
                          · {sc.n.toLocaleString('en-US')} sold
                        </span>
                      </span>
                      <span
                        className={cn(
                          'font-semibold tabular-nums',
                          negative ? 'text-destructive' : 'text-foreground',
                        )}
                      >
                        {money(sc.net)}
                      </span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn('h-full', negative ? 'bg-destructive' : 'bg-primary')}
                        style={{ width: `${width}%` }}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      </div>

      {/* CTA */}
      <div className="overflow-hidden rounded-2xl bg-linear-to-br from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary p-6 text-center sm:p-8">
        <h2 className="text-xl font-semibold tracking-tight text-white">
          Ready to put this plan to work?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm font-medium text-white/85">
          Create your organization on Gala Season and start selling tickets and sponsorships for
          your next event.
        </p>
        <Button size="lg" variant="secondary" asChild className="mt-4 gap-1.5">
          <Link href="/organizations/sign_up">
            Create your event
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>

      <p className="text-center text-xs text-muted-foreground">
        Planning estimates only — not tax or financial advice.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Field sub-components
// ---------------------------------------------------------------------------

type FieldProps = {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
}

function MoneyField({ id, label, value, onChange, hint }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      <div className="relative">
        <span
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground"
          aria-hidden="true"
        >
          $
        </span>
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 pl-7 tabular-nums"
        />
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function NumberField({ id, label, value, onChange, hint }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 tabular-nums"
      />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function PercentField({ id, label, value, onChange }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 pr-7 tabular-nums"
        />
        <span
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground"
          aria-hidden="true"
        >
          %
        </span>
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className={EYEBROW}>{label}</p>
      <p className="mt-1 font-serif text-xl font-semibold tracking-tight tabular-nums">{value}</p>
    </div>
  )
}

type CellInputProps = React.ComponentProps<typeof Input> & { align?: 'left' | 'right' }

function CellInput({ align = 'left', className, ...props }: CellInputProps) {
  return (
    <Input
      {...props}
      className={cn(
        'h-8 rounded-md border-0 bg-transparent px-1.5 text-sm shadow-none tabular-nums hover:bg-muted/60 focus-visible:bg-card focus-visible:ring-1',
        align === 'right' && 'text-right',
        className,
      )}
    />
  )
}
